import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  notification,
  message,
  Tag,
  Space
} from "antd";
import { useSelector } from "react-redux";
import { fetchCoupons } from "./fetchCoupons";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import { token } from "../../constants/Constants";
import { create_coupon, toggle_coupon_status, update_coupon } from "../../endpoints/API";
import dayjs from "dayjs";

const { Option } = Select;

const Coupons = ({ couponsData }) => {
  const [isCreating, setIsCreating] = useState(false);
  const isUser = useSelector((state) => state.user.user);
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const [coupons, setCoupons] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [form] = Form.useForm();

  const handleEdit = (record) => {
    setIsCreating(true);
    setIsEditing(true);
    setEditingCoupon(record);

    form.setFieldsValue({
      ...record,
      expiry: record.expiry ? dayjs(record.expiry) : null,
    });
  };

  useEffect(() => {
    const getCoupons = async () => {
      try {
        await fetchCoupons(setCoupons, baseUrl, prodTestUrl);
      } catch (error) {
        message.error("Failed to fetch containers.");
      }
    };

    getCoupons();
  }, []);

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Discount Type",
      dataIndex: "discount_type",
      key: "discount_type",
      render: (text) =>
        text === "percentage" ? "Percentage Discount" : "Money Discount",
      responsive: ["md"],
    },
    {
      title: "Discount Value",
      dataIndex: "discount_value",
      key: "discount_value",
      render: (text, record) => {
        if (record.discount_type === "money") {
          return `${parseInt(text)}`;
        } else if (record.discount_type === "percentage") {
          return `${text}%`;
        }
        return text;
      },
    },
    {
      title: "Min. Amount",
      dataIndex: "minimum_purchase_amount",
      key: "minimum_purchase_amount",
      render: (text) => {
        return `${parseInt(text)}`;
      },
      responsive: ["sm"],
    },
    {
      title: "Expiry Date",
      dataIndex: "expiry",
      key: "expiry",
      render: (date) => {
        const formattedDate = new Date(date);
        const day = String(formattedDate.getDate()).padStart(2, "0");
        const month = String(formattedDate.getMonth() + 1).padStart(2, "0");
        const year = formattedDate.getFullYear();
        return `${day}-${month}-${year}`;
      },
      responsive: ["sm"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "active" ? "green" : "red"}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            disabled={record.status === "inactive"}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>

          <Button
            size="small"
            danger={record.status === "active"}
            onClick={async () => {
              try {
                await toggle_coupon_status(record.id);
                await fetchCoupons(setCoupons, baseUrl, prodTestUrl);
                message.success("Status updated");
              } catch {
                message.error("Failed to update status");
              }
            }}
          >
            {record.status === "active" ? "Deactivate" : "Activate"}
          </Button>
        </Space>
      ),
    },
  ];

  const handleFinish = async (values) => {
    const payload = {
      ...values,
      expiry: values.expiry.format("YYYY-MM-DD HH:mm:ss"),
    };

    try {
      if (isEditing) {
        await update_coupon(editingCoupon.id, payload);
        message.success("Coupon updated successfully");
      } else {
        payload.created_by = isUser.user_id;
        await create_coupon(payload);
        message.success("Coupon created successfully");
      }

      await fetchCoupons(setCoupons, baseUrl, prodTestUrl);

      form.resetFields();
      setIsCreating(false);
      setIsEditing(false);
      setEditingCoupon(null);
    } catch (error) {
      message.error("Operation failed");
    }
  };

  return (
    <div style={{ padding: "12px" }}>
      {!isCreating ? (
        <>
          <div style={{ textAlign: "right", marginBottom: "16px" }}>
            <Button type="primary" onClick={() => setIsCreating(true)}>
              Create Coupon
            </Button>
          </div>

          {/* Scrollable wrapper for table on small screens */}
          <div style={{ overflowX: "auto", width: "100%" }}>
            <Table
              dataSource={coupons?.data}
              columns={columns}
              rowKey="id"
              scroll={{ x: "max-content" }}
              size="small"
              pagination={{
                responsive: true,
                showSizeChanger: false,
                size: "small",
              }}
            />
          </div>
        </>
      ) : (
        <div style={{ overflowX: "hidden", width: "100%" }}>
          <Form
            layout="vertical"
            form={form}
            onFinish={handleFinish}
            style={{ maxWidth: "600px", margin: "0 auto", width: "100%" }}
          >
            <Form.Item
              label="Code"
              name="code"
              rules={[
                { required: true, message: "Please enter the coupon code!" },
              ]}
            >
              <Input disabled={isEditing} placeholder="Enter coupon code" />
            </Form.Item>

            <Form.Item
              label="Discount Type"
              name="discount_type"
              rules={[
                { required: true, message: "Please select the discount type!" },
              ]}
            >
              <Select placeholder="Select discount type">
                <Option value="percentage">Percentage Discount</Option>
                <Option value="money">Money Discount</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Discount Value"
              name="discount_value"
              rules={[
                { required: true, message: "Please enter the discount value!" },
              ]}
            >
              <Input type="number" placeholder="Enter discount value" />
            </Form.Item>

            <Form.Item
              label="Minimum Purchase Amount"
              name="minimum_purchase_amount"
              rules={[
                {
                  required: true,
                  message: "Please enter the minimum purchase amount!",
                },
              ]}
            >
              <Input type="number" placeholder="Enter minimum purchase amount" />
            </Form.Item>

            <Form.Item
              label="Expiry Date"
              name="expiry"
              rules={[
                { required: true, message: "Please select the expiry date!" },
              ]}
            >
              <DatePicker
                placeholder="Select expiry date and time"
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item>
              <Space wrap>
                <Button type="primary" htmlType="submit">
                  {isEditing ? "Update" : "Submit"}
                </Button>
                <Button
                  onClick={() => {
                    form.resetFields();
                    setIsCreating(false);
                    setIsEditing(false);
                    setEditingCoupon(null);
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .ant-table {
            font-size: 12px !important;
          }
          .ant-table-cell {
            padding: 6px 8px !important;
          }
          .ant-btn-sm,
          .ant-btn-link {
            font-size: 12px !important;
            padding: 0 6px !important;
          }
          .ant-tag {
            font-size: 11px !important;
            padding: 0 4px !important;
          }
          .ant-form-item {
            margin-bottom: 12px !important;
          }
          .ant-picker {
            width: 100% !important;
          }
        }

        @media (max-width: 480px) {
          .ant-space-item .ant-btn {
            margin-bottom: 4px;
          }
        }
      `}</style>
    </div>
  );
};

export default Coupons;